import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ChatResponse, ChatSession } from '@/types';
import { CognitoAuthService } from './cognito-auth';

const API_URL = import.meta.env.VITE_API_URL;

// Callback for handling logout when refresh token expires
let onLogoutCallback: (() => void) | null = null;

export const setLogoutCallback = (callback: () => void) => {
  onLogoutCallback = callback;
};

// Helper to get a fresh ID token (refreshes if needed)
export const getFreshIdToken = async (): Promise<string | null> => {
  try {
    // First try to get current token
    const currentToken = await CognitoAuthService.getIdToken();
    if (currentToken) {
      return currentToken;
    }

    // Token not available or expired, try refreshing
    const session = await CognitoAuthService.refreshSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error('Failed to get fresh token:', error);
    // If refresh fails, token is likely expired - trigger logout
    if (onLogoutCallback) {
      onLogoutCallback();
    }
    return null;
  }
};

// Create API client factory with automatic token refresh
export const createApiClient = () => {
  const client = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor: add fresh token to every request
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await getFreshIdToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: handle Lambda unwrapping and 401 errors
  client.interceptors.response.use(
    (response) => {
      // If response has Lambda structure (statusCode, headers, body), unwrap it
      if (response.data && typeof response.data === 'object' && 'body' in response.data && 'statusCode' in response.data) {
        // Parse the body string into JSON
        const parsedBody = typeof response.data.body === 'string'
          ? JSON.parse(response.data.body)
          : response.data.body;

        // Replace response.data with the parsed body
        response.data = parsedBody;
      }
      return response;
    },
    async (error: AxiosError) => {
      // Handle error responses that might also have Lambda structure
      if (error.response?.data && typeof error.response.data === 'object' && 'body' in error.response.data) {
        const parsedBody = typeof error.response.data.body === 'string'
          ? JSON.parse(error.response.data.body)
          : error.response.data.body;
        error.response.data = parsedBody;
      }

      // Handle 401 Unauthorized - try to refresh token
      if (error.response?.status === 401) {
        try {
          // Attempt to refresh the session
          await CognitoAuthService.refreshSession();

          // Retry the original request with the new token
          const token = await getFreshIdToken();
          if (token && error.config) {
            error.config.headers.Authorization = `Bearer ${token}`;
            return client.request(error.config);
          }
        } catch (refreshError) {
          // Refresh failed - logout user
          console.error('Token refresh failed:', refreshError);
          if (onLogoutCallback) {
            onLogoutCallback();
          }
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Legacy client for backward compatibility (will use window auth context)
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class ChatAPI {
  /**
   * Send a chat message
   */
  static async sendMessage(
    message: string,
    sessionId?: string
  ): Promise<ChatResponse> {
    const response = await apiClient.post<ChatResponse>('/chat', {
      message,
      session_id: sessionId,
    });
    return response.data;
  }

  /**
   * Get chat history for a session
   */
  static async getChatHistory(sessionId: string): Promise<{
    session_id: string;
    messages: Array<{
      role: string;
      content: string;
      timestamp: string;
    }>;
  }> {
    const response = await apiClient.get(`/chat/history/${sessionId}`);
    return response.data;
  }

  /**
   * Get all user sessions
   */
  static async getUserSessions(): Promise<{
    user_id: string;
    sessions: ChatSession[];
  }> {
    const response = await apiClient.get('/chat/sessions');
    return response.data;
  }
}

export { apiClient };
